import {
    Typography,
    Box,
    Paper,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useState, useEffect } from 'react';
import { usersApi, User } from '../services/users-api.service';

interface UsersListProps {
    onEditUser?: (user: User) => void;
    refreshTrigger?: number;
}

export function UsersList({ onEditUser, refreshTrigger }: UsersListProps) {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await usersApi.listUsers({ pageSize: 100 });

            if (response.success) {
                setUsers(response.data || []);
            } else {
                setError('Failed to fetch users');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        if (refreshTrigger && refreshTrigger > 0) {
            fetchUsers();
        }
    }, [refreshTrigger]);

    const handleEditUser = (user: User) => {
        onEditUser?.(user);
    };

    const getRoleColor = (role: string): 'error' | 'warning' | 'info' | 'default' => {
        switch (role) {
            case 'admin':
                return 'error';
            case 'finance':
                return 'warning';
            case 'teacher':
                return 'info';
            default:
                return 'default';
        }
    };

    const getStatusColor = (status: string): 'success' | 'error' => {
        return status === 'active' ? 'success' : 'error';
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-CA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                    {loading ? 'Loading...' : `${users.length} users loaded`}
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 700 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ py: 1 }}>Email</TableCell>
                                <TableCell sx={{ py: 1 }}>First Name</TableCell>
                                <TableCell sx={{ py: 1 }}>Last Name</TableCell>
                                <TableCell sx={{ py: 1 }}>Role</TableCell>
                                <TableCell sx={{ py: 1 }}>Status</TableCell>
                                <TableCell sx={{ py: 1 }}>Created</TableCell>
                                <TableCell sx={{ py: 1 }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow
                                    key={user.id}
                                    sx={{
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        '&:hover': { backgroundColor: 'action.hover' },
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                    }}
                                    onDoubleClick={() => handleEditUser(user)}
                                >
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.firstName || '--'}</TableCell>
                                    <TableCell>{user.lastName || '--'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={user.role}
                                            size="small"
                                            color={getRoleColor(user.role)}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={user.status}
                                            size="small"
                                            color={getStatusColor(user.status)}
                                            variant="filled"
                                        />
                                    </TableCell>
                                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                                    <TableCell>
                                        <Tooltip title="Edit User">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditUser(user)}
                                                color="primary"
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                        <CircularProgress size={24} />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            Loading users...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && users.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No users found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </>
    );
}
